import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, InlineError, Skeleton } from '../../components/ui';
import { SectionRow } from '../../components/ui/SectionRow';
import { useAuth } from '../auth/AuthProvider';

/**
 * The permissions matrix: people down, modules across, a level in every cell.
 *
 * A grid rather than a form per person, because the question people actually
 * ask is "who can reach Sales Tracker" as often as "what can Nez reach", and
 * only a grid answers both without navigating.
 *
 * WHAT THIS CONTROLS TODAY: navigation and this screen. The 225 existing
 * policy call sites still decide database access until the refactor lands, so
 * setting somebody to No access hides a module rather than sealing it. The
 * banner says so, because a permission screen that overstates its own reach is
 * worse than none — it invites people to rely on it.
 */

const MODULES: { key: string; label: string }[] = [
  { key: 'projects', label: 'Projects' },
  { key: 'nurture_studio', label: 'Nurture Studio' },
  { key: 'social_studio', label: 'Social Studio' },
  { key: 'sales_tracker', label: 'Sales Tracker' },
  { key: 'deals', label: 'Deals' },
  { key: 'cg_orders', label: 'CG Orders' },
  { key: 'supplier_reviews', label: 'Supplier Reviews' },
  { key: 'files', label: 'Files' },
  { key: 'admin', label: 'Admin' },
];

type Level = 'no_access' | 'member' | 'admin' | 'super_admin';

const LEVELS: { value: Level; label: string }[] = [
  { value: 'no_access', label: 'No access' },
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const LEVEL_ERRORS: Record<string, string> = {
  owner_permissions_are_immutable: 'The owner’s access cannot be changed.',
  only_super_admin_may_set_permissions: 'Only a Super Admin can change permissions.',
  last_super_admin_must_remain: 'Someone has to stay a Super Admin.',
};

interface MatrixRow {
  user_id: string;
  display_name: string;
  email: string;
  is_owner: boolean;
  module_key: string;
  access_level: Level;
}

function nameOf(row: { display_name: string; email: string }): string {
  const name = row.display_name.trim();
  if (name !== '') {
    return name;
  }
  const local = row.email.split('@')[0] ?? row.email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function PermissionsMatrix() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  /** Cells mid-flight, so the grid can show the new value immediately. */
  const [pending, setPending] = useState<Record<string, Level>>({});

  const matrix = useQuery({
    queryKey: ['permissions-matrix'],
    queryFn: async () => {
      const { data, error: cause } = await client
        .from('user_module_permissions')
        .select(
          'user_id, module_key, access_level, profiles:user_id (display_name, email, is_owner)',
        );
      if (cause) {
        throw new Error(cause.message);
      }
      return (data ?? []).map((row) => {
        const profile = row.profiles as unknown as {
          display_name: string;
          email: string;
          is_owner: boolean;
        } | null;
        return {
          user_id: row.user_id,
          module_key: row.module_key,
          access_level: row.access_level,
          display_name: profile?.display_name ?? '',
          email: profile?.email ?? '',
          is_owner: profile?.is_owner ?? false,
        } satisfies MatrixRow;
      });
    },
    retry: false,
  });

  const setLevel = useMutation({
    mutationFn: async (input: { userId: string; moduleKey: string; level: Level }) => {
      const { error: cause } = await client
        .from('user_module_permissions')
        .upsert(
          { user_id: input.userId, module_key: input.moduleKey, access_level: input.level },
          { onConflict: 'user_id,module_key' },
        );
      if (cause) {
        throw new Error(cause.message);
      }
    },
    onSuccess: () => {
      setError('');
      void queryClient.invalidateQueries({ queryKey: ['permissions-matrix'] });
    },
    onError: (cause: Error, input) => {
      // The optimistic value is rolled back: the database refused, so showing
      // the new level would be a lie about what is stored.
      setPending((current) => {
        const next = { ...current };
        delete next[`${input.userId}:${input.moduleKey}`];
        return next;
      });
      const key = Object.keys(LEVEL_ERRORS).find((code) => cause.message.includes(code));
      setError(
        (key === undefined ? undefined : LEVEL_ERRORS[key]) ?? 'That change could not be saved.',
      );
    },
  });

  // A non-Super-Admin sees only their own row, which is not a matrix. Show
  // nothing rather than a grid of one.
  const rows = matrix.data ?? [];
  const people = [...new Map(rows.map((row) => [row.user_id, row])).values()].sort((left, right) =>
    nameOf(left).localeCompare(nameOf(right)),
  );
  if (matrix.isError || (matrix.data !== undefined && people.length <= 1)) {
    return null;
  }

  const levelAt = (userId: string, moduleKey: string): Level =>
    pending[`${userId}:${moduleKey}`] ??
    rows.find((row) => row.user_id === userId && row.module_key === moduleKey)?.access_level ??
    'no_access';

  return (
    /* A section row rather than a Panel: the admin page had three stacked
       filled boxes, which is the container fatigue this overhaul removes. The
       explanatory paragraph moves into the anchor column, where it reads as
       the section's subtitle rather than as a preamble the table pushes down
       the page. */
    <SectionRow
      title="Module permissions"
      subtitle="Changes save as you make them and are recorded. These levels control what appears in navigation; database access is still governed by the existing rules until the policy refactor lands."
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {matrix.isPending ? (
        <Skeleton lines={5} label="Loading permissions" />
      ) : (
        <div className="matrix-scroll">
          <table className="ui-table matrix-table">
            <thead>
              <tr>
                {/* Sticky on BOTH axes. The name column below is pinned left;
                    without this its own label scrolled away and a module
                    heading slid into the gap, so the pinned column ended up
                    captioned by whatever happened to be passing. */}
                <th scope="col" className="matrix-person--head">
                  Person
                </th>
                {MODULES.map((module) => (
                  <th key={module.key} scope="col">
                    {module.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.user_id}>
                  <th scope="row" className="matrix-person">
                    {nameOf(person)}
                    {person.is_owner ? <Badge micro>Owner</Badge> : null}
                  </th>
                  {MODULES.map((module) => {
                    const value = levelAt(person.user_id, module.key);
                    return (
                      <td key={module.key}>
                        {person.is_owner ? (
                          // Not a disabled dropdown: there is nothing to
                          // choose, and a greyed control invites the attempt.
                          <span className="matrix-fixed">Super Admin</span>
                        ) : (
                          <select
                            className="matrix-select"
                            data-level={value}
                            aria-label={`${module.label} access for ${nameOf(person)}`}
                            value={value}
                            onChange={(event) => {
                              const level = event.target.value as Level;
                              setPending((current) => ({
                                ...current,
                                [`${person.user_id}:${module.key}`]: level,
                              }));
                              setLevel.mutate({
                                userId: person.user_id,
                                moduleKey: module.key,
                                level,
                              });
                            }}
                          >
                            {LEVELS.map((level) => (
                              <option key={level.value} value={level.value}>
                                {level.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionRow>
  );
}
